import { Form } from "@circle-react-uikit/Form";
import { isEmpty } from "lodash";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import type { FieldElement as FieldElementType } from "@/react/types/Element";

import { useFieldElementRules } from "../useFieldElementRules";

interface FieldElementProps {
  className?: string;
  defaultValue?: string;
  disabled?: boolean;
  element: FieldElementType;
  errors: Record<number, string>;
  fieldIndex: number;
  initialFocusRef?: React.RefObject<HTMLInputElement | null>;
  isFirstChild?: boolean;
}
export const FieldElement = ({
  element,
  initialFocusRef,
  fieldIndex,
  isFirstChild,
  errors,
  className,
  disabled = false,
  defaultValue,
}: FieldElementProps) => {
  const { setError, setValue } = useFormContext();
  const rules = useFieldElementRules(element);
  let label = element.attributes.label.trim();

  if (element.attributes.field_type === "contact_email") {
    // When the field type is "contact_email", we force "email" if not provided.
    label = label || t("settings.forms.default_contact_email_label");
  } else if (element.attributes.field_type === "contact_name") {
    // When the field type is "contact_name", we also force a default label if not provided.
    label = label || t("settings.forms.default_contact_name_label");
  }

  const idName = `elements[${fieldIndex}][id]`;
  const valueName = `elements[${fieldIndex}][value]`;

  useEffect(() => {
    if (errors && errors[fieldIndex]) {
      setError(`elements[${fieldIndex}][value]`, {
        type: "server",
        message: errors[fieldIndex],
      });
    }
  }, [errors, fieldIndex, setError]);

  // Set the value programmatically when defaultValue is provided
  useEffect(() => {
    if (defaultValue) {
      setValue(valueName, defaultValue);
    }
  }, [defaultValue, setValue, valueName]);

  if (element.attributes.hidden) {
    return null;
  }

  return (
    <>
      <Form.Item
        id={valueName}
        isFirstChild={isFirstChild}
        name={valueName}
        hideDescription
        hideBorder
        hideLabel={isEmpty(label)}
        label={label}
        labelClassName="[overflow-wrap:anywhere]"
        rules={rules}
        forceRequiredLabel={element.attributes.required}
        className={className}
        errorClassName="form-group__error !mb-0"
        placeholder={
          element.attributes.placeholder ||
          t("settings.forms.edit.defaults.field_placeholder", {
            field_label: label.toLowerCase(),
          })
        }
      >
        {element.attributes.field_type === "long_answer" ? (
          <Form.TextArea name={valueName} />
        ) : (
          <Form.Input
            type={
              element.attributes.field_type === "number" ? "number" : "text"
            }
            name={valueName}
            autoComplete="off"
            ref={initialFocusRef}
            disabled={disabled}
          />
        )}
      </Form.Item>
      <Form.Input
        wrapperClassName="hidden"
        type="hidden"
        name={idName}
        value={element.id}
      />
    </>
  );
};
